import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { ChevronsUpDown, ChevronUp, ChevronDown, Plus, Loader2, MoreVertical, GitMerge } from 'lucide-react';
import usePermissions from '../../auth/usePermissions';
import '../../component-styles/RecordPages.css';
import API_BASE from '../../utils/api.js';
import PurchaseOrderFolioModal from '../../components/Projects/PurchaseOrders/PurchaseOrderFolioModal';
import ClientViewedBadge from '../../components/shared/ClientViewedBadge';
import RecordPagination from '../../components/shared/RecordPagination';
import usePageSize from '../../hooks/usePageSize';
import notify from '../../utils/notify';
import ConfirmModal from '../../components/utils/ConfirmModal';
import {
  autoCreateQuickBooksPurchaseOrder,
  autoUpdateQuickBooksPurchaseOrder,
} from '../../utils/quickbooksAutoSync.js';

/* ---------------------- Helpers ---------------------- */
function getApiBase() {
  return API_BASE;
}

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function getPOOpenAmount(po) {
  return Math.max(0, Number(po?.total || 0) - Number(po?.payments || 0));
}

function SortIcon({ active, order }) {
  if (!active) return <ChevronsUpDown size={14} className="sort-icon neutral" />;
  return order === 'asc' ? (
    <ChevronUp size={14} className="sort-icon active" />
  ) : (
    <ChevronDown size={14} className="sort-icon active" />
  );
}

function getQuickBooksBillUrl(billId, env = 'sandbox') {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';
  return `${base}/app/bill?txnId=${billId}`;
}

/* ---------------------- Dropdown ---------------------- */
function DropdownMenu({ qbBillUrl, onSyncQuickBooks, syncingQuickBooks, onDownloadPdf, onDelete, canDelete }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    function close(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const close = () => setOpen(false);

  return (
    <div className="menu-container" ref={ref}>
      <button
        className="menu-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <MoreVertical size={16} />
      </button>

      <div className={`menu-dropdown ${open ? 'open' : ''}`}>
        {onSyncQuickBooks && (
          <button
            disabled={syncingQuickBooks}
            onClick={(e) => {
              e.stopPropagation();
              close();
              onSyncQuickBooks();
            }}
          >
            {syncingQuickBooks ? 'Syncing…' : 'Sync to QuickBooks'}
          </button>
        )}
        {qbBillUrl && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              close();
              window.open(qbBillUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            Open QuickBooks Bill
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            close();
            onDownloadPdf();
          }}
        >
          Download PDF
        </button>
        {canDelete && (
          <button
            className="menu-danger"
            onClick={(e) => {
              e.stopPropagation();
              close();
              onDelete();
            }}
          >
            Delete Purchase Order
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------------- Main Component ---------------------- */
export default function PurchaseOrdersSection({ projectId: propProjectId, project: propProject }) {
  const outlet = useOutletContext?.() || {};
  const projectId = propProjectId || outlet.projectId || '';
  const project = propProject || outlet.project || null;

  const { permissions } = usePermissions();
  const canCreate = permissions.createEditPurchaseOrders !== false;

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('purchaseOrders');

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const [showFolio, setShowFolio] = useState(null);
  const [syncingPoId, setSyncingPoId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [merging, setMerging] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirm', destructive: false, pendingAction: null });

  function askConfirm({ title, message, confirmLabel, destructive, action }) {
    setConfirmModal({ open: true, title, message, confirmLabel, destructive: !!destructive, pendingAction: action });
  }

  async function handleConfirm() {
    const action = confirmModal.pendingAction;
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
    if (action) await action();
  }

  function handleCancel() {
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
  }

  const navigate = useNavigate();
  const API_BASE = getApiBase();
  const userId = localStorage.getItem('userId');

  /* ---------------------- Fetch ---------------------- */
  const fetchPOs = useCallback(
    async (bg = false) => {
      if (!projectId || !userId) return;
      bg ? setIsRefreshing(true) : setLoading(true);

      try {
        const url = `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`;
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const json = await res.json();
        const arr = Array.isArray(json) ? json : [];

        const normalized = arr.map((po) => ({
          id: po.poId || po.id || po.orderNumber || String(Math.random()),
          date: po.date || po.createdAt || '',
          orderNumber: po.orderNumber || po.poId || '',
          title: po.title || '',
          vendorName: po.vendorName || '',
          status: po.status || 'Draft',
          total: Number(po.total || 0),
          payments: Number(po.payments || 0),
          projectId: po.projectId,
          raw: po,
        }));

        setPurchaseOrders(normalized);
      } catch (err) {
        console.error('Failed to fetch purchase orders:', err);
        if (!bg) setPurchaseOrders([]);
      } finally {
        bg ? setIsRefreshing(false) : setLoading(false);
      }
    },
    [API_BASE, projectId, userId]
  );

  useEffect(() => {
    fetchPOs();
    const i = setInterval(() => fetchPOs(true), 30000);
    return () => clearInterval(i);
  }, [fetchPOs]);

  /* ---------------------- Totals ---------------------- */
  const totals = useMemo(() => {
    const totalPO = purchaseOrders.reduce((s, p) => s + getPOOpenAmount(p), 0);
    const paidPO = purchaseOrders.reduce((s, p) => s + (Number(p.payments) || 0), 0);
    const originalTotal = purchaseOrders.reduce((s, p) => s + (Number(p.total) || 0), 0);
    return { totalPO, paidPO, originalTotal };
  }, [purchaseOrders]);

  /* ---------------------- Sorting + Filtering ---------------------- */
  const filtered = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();

    let arr = purchaseOrders.filter((p) => {
      if (!q) return true;
      return (
        (p.orderNumber || '').toLowerCase().includes(q) ||
        (p.title || '').toLowerCase().includes(q) ||
        (p.vendorName || '').toLowerCase().includes(q) ||
        (p.status || '').toLowerCase().includes(q)
      );
    });

    arr.sort((a, b) => {
      const get = (it, field) => {
        if (!it) return '';
        if (field === 'date') return it.date ? new Date(it.date).getTime() : 0;
        if (field === 'total') return getPOOpenAmount(it);
        if (field === 'payments') return Number(it[field] || 0);
        return (it[field] || '').toString().toLowerCase();
      };
      const va = get(a, sortField);
      const vb = get(b, sortField);
      if (va === vb) return 0;
      return sortOrder === 'asc' ? (va < vb ? -1 : 1) : va > vb ? -1 : 1;
    });

    return arr;
  }, [purchaseOrders, searchQuery, sortField, sortOrder]);

  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  function toggleSort(field) {
    if (sortField === field) setSortOrder((s) => (s === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  }

  async function syncToQuickBooks(po) {
    const poId = po?.raw?.poId;
    if (!poId || !projectId) {
      notify('Unable to sync purchase order to QuickBooks.');
      return;
    }
    if (!localStorage.getItem('idToken')) {
      notify('You must be signed in to sync with QuickBooks.');
      return;
    }

    setSyncingPoId(poId);
    try {
      const alreadySynced = Boolean(po.raw?.quickbooks?.billId);
      let qbResult;
      try {
        if (alreadySynced) {
          qbResult = await autoUpdateQuickBooksPurchaseOrder({ poId, projectId });
        } else {
          qbResult = await autoCreateQuickBooksPurchaseOrder({ poId, projectId });
        }
      } catch (err) {
        if (!alreadySynced && /already synced as a quickbooks bill/i.test(err.message || '')) {
          qbResult = await autoUpdateQuickBooksPurchaseOrder({ poId, projectId });
        } else {
          throw err;
        }
      }
      if (qbResult?.skipped) {
        notify.warning(qbResult.warning);
        return;
      }
      await fetchPOs(true);
      notify('QuickBooks bill synced.');
    } catch (err) {
      console.error('QuickBooks PO sync error:', err);
      notify(err.message || 'Failed to sync purchase order to QuickBooks.');
    } finally {
      setSyncingPoId(null);
    }
  }

  /* ---------------------- Merge ---------------------- */
  // Drop selections whose PO is gone (deleted, merged away, project switched).
  useEffect(() => {
    setSelectedIds((prev) => {
      const live = new Set(purchaseOrders.map((po) => po.raw.poId));
      const next = prev.filter((id) => live.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [purchaseOrders]);

  const selectedPOs = useMemo(
    () => purchaseOrders.filter((po) => selectedIds.includes(po.raw.poId)),
    [purchaseOrders, selectedIds]
  );

  // Mirrors the backend's guards so the reason shows up before the round trip.
  const mergeIssue = useMemo(() => {
    if (selectedPOs.length < 2) return 'Select two or more purchase orders to merge.';
    const vendors = new Set(selectedPOs.map((po) => (po.vendorName || '').trim().toLowerCase()));
    if (vendors.has('')) return 'Every selected purchase order needs a vendor before merging.';
    if (vendors.size > 1) return 'Only purchase orders for the same vendor can be merged.';
    if (selectedPOs.some((po) => Number(po.payments || 0) > 0)) {
      return 'Purchase orders with payments applied cannot be merged.';
    }
    return '';
  }, [selectedPOs]);

  // Same rule the backend defaults to: keep the earliest order number, since
  // that is the one the vendor most likely already has.
  const mergeTarget = useMemo(() => {
    if (selectedPOs.length < 2) return null;
    const orderNumberValue = (po) => {
      const parsed = Number(String(po.orderNumber || '').replace(/\D/g, ''));
      return Number.isFinite(parsed) && parsed > 0 ? parsed : Number.POSITIVE_INFINITY;
    };
    return selectedPOs
      .slice()
      .sort(
        (a, b) =>
          orderNumberValue(a) - orderNumberValue(b) ||
          String(a.raw.createdAt || '').localeCompare(String(b.raw.createdAt || ''))
      )[0];
  }, [selectedPOs]);

  function toggleSelected(poId) {
    setSelectedIds((prev) =>
      prev.includes(poId) ? prev.filter((id) => id !== poId) : [...prev, poId]
    );
  }

  function mergeSelected() {
    if (mergeIssue || !mergeTarget) return;
    const absorbed = selectedPOs.filter((po) => po.raw.poId !== mergeTarget.raw.poId);
    const keepLabel = mergeTarget.orderNumber || 'the earliest purchase order';

    askConfirm({
      title: 'Merge Purchase Orders',
      message: `Move all items, freight and tax from ${absorbed
        .map((po) => po.orderNumber || po.raw.poId)
        .join(', ')} onto ${keepLabel}? The merged-in purchase order${
        absorbed.length === 1 ? '' : 's'
      } will be deleted, and ${keepLabel} will cover every source invoice.`,
      confirmLabel: 'Merge',
      destructive: true,
      action: async () => {
        setMerging(true);
        try {
          const res = await fetch(
            `${API_BASE}/api/purchase-orders/${encodeURIComponent(projectId)}/merge`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                poIds: selectedPOs.map((po) => po.raw.poId),
                targetPoId: mergeTarget.raw.poId,
              }),
            }
          );
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            notify(data.error || 'Failed to merge purchase orders.');
            return;
          }
          setSelectedIds([]);
          await fetchPOs(true);
          notify(`Merged ${absorbed.length + 1} purchase orders into ${keepLabel}.`);
        } catch (err) {
          console.error('Merge PO error', err);
          notify('Unable to merge purchase orders.');
        } finally {
          setMerging(false);
        }
      },
    });
  }

  async function deleteOne(po) {
    askConfirm({
      title: 'Delete Purchase Order',
      message: `Permanently delete purchase order ${po.orderNumber || ''}? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      action: async () => {
        try {
          const res = await fetch(
            `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/by-id/${encodeURIComponent(po.raw.poId)}`,
            { method: 'DELETE' }
          );
          const data = await res.json();
          if (!res.ok) {
            notify(data.error || 'Failed to delete purchase order.');
            return;
          }
          fetchPOs(true);
        } catch (err) {
          console.error('Delete PO error', err);
          notify('Unable to delete purchase order.');
        }
      },
    });
  }

  /* ---------------------- Render ---------------------- */
  return (
    <section className="record-section">
      <div className="record-top-row">
        <h2 className="record-title">Purchase Orders</h2>

        <div className="record-controls">
          <input
            className="record-search"
            placeholder="Search order #, vendor, status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search purchase orders"
          />
          {canCreate && (
            <button
              className="record-add-btn"
              onClick={() => navigate(`/projects/${projectId}/purchase-orders/new`)}
              title="Add new purchase order"
            >
              <Plus size={14} /> <span>Add New</span>
            </button>
          )}
        </div>
      </div>

      <div className="record-card">
        <div className="record-metrics">
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.totalPO)}</div>
            <div className="record-metric-label">Total Outstanding</div>
          </div>
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.paidPO)}</div>
            <div className="record-metric-label">Paid Purchase Orders</div>
          </div>
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.originalTotal)}</div>
            <div className="record-metric-label">Original Total</div>
          </div>
        </div>

        {isRefreshing && (
          <div className="record-refresh-banner">
            <Loader2 className="spin" size={14} /> Refreshing…
          </div>
        )}

        {canCreate && selectedIds.length > 0 && (
          <div className="record-bulk-bar">
            <span className="record-bulk-count">
              {selectedIds.length} selected
              {mergeIssue && selectedIds.length >= 2 ? ` — ${mergeIssue}` : ''}
            </span>
            <div className="record-bulk-actions">
              <button
                className="record-bulk-btn"
                onClick={mergeSelected}
                disabled={!!mergeIssue || merging}
                title={mergeIssue || `Merge into ${mergeTarget?.orderNumber || ''}`}
              >
                <GitMerge size={14} />{' '}
                {merging
                  ? 'Merging…'
                  : `Merge into ${mergeTarget?.orderNumber || 'earliest PO'}`}
              </button>
              <button className="record-bulk-btn ghost" onClick={() => setSelectedIds([])}>
                Clear
              </button>
            </div>
          </div>
        )}

        <div className="record-table-wrap">
          {loading ? (
            <div className="record-empty">
              <div className="record-empty-icon" aria-hidden="true">...</div>
              <div className="record-empty-title">Loading purchase orders…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="record-empty">
              <div className="record-empty-icon" aria-hidden="true">[ ]</div>
              <div className="record-empty-title">No purchase orders yet</div>
              <div className="record-empty-sub">Create your first purchase order for this project.</div>
              {canCreate && (
                <button
                  className="record-empty-cta"
                  onClick={() => navigate(`/projects/${projectId}/purchase-orders/new`)}
                >
                  <Plus size={14} /> Add New Purchase Order
                </button>
              )}
            </div>
          ) : (
            <table className="record-table" role="table" aria-label="Purchase orders">
              <thead>
                <tr>
                  {canCreate && (
                    <th className="record-select-cell">
                      <input
                        type="checkbox"
                        aria-label="Select all purchase orders on this page"
                        checked={
                          paginated.length > 0 &&
                          paginated.every((po) => selectedIds.includes(po.raw.poId))
                        }
                        onChange={(e) => {
                          const pageIds = paginated.map((po) => po.raw.poId);
                          setSelectedIds((prev) =>
                            e.target.checked
                              ? [...prev, ...pageIds.filter((id) => !prev.includes(id))]
                              : prev.filter((id) => !pageIds.includes(id))
                          );
                        }}
                      />
                    </th>
                  )}
                  <th onClick={() => toggleSort('date')}>
                    PO Date <SortIcon active={sortField === 'date'} order={sortOrder} />
                  </th>
                  <th onClick={() => toggleSort('orderNumber')}>
                    Order # <SortIcon active={sortField === 'orderNumber'} order={sortOrder} />
                  </th>
                  <th onClick={() => toggleSort('vendorName')}>
                    Vendor <SortIcon active={sortField === 'vendorName'} order={sortOrder} />
                  </th>
                  <th onClick={() => toggleSort('status')}>
                    Status <SortIcon active={sortField === 'status'} order={sortOrder} />
                  </th>
                  <th onClick={() => toggleSort('total')}>
                    Balance Due<SortIcon active={sortField === 'total'} order={sortOrder} />
                  </th>
                  <th onClick={() => toggleSort('payments')}>
                    Payments <SortIcon active={sortField === 'payments'} order={sortOrder} />
                  </th>
                  <th>Original Total</th>
                  <th>Folio</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((po) => {
                  const open = getPOOpenAmount(po);
                  const statusClass = (po.status || 'draft').toLowerCase().replace(/\s+/g, '-');

                  return (
                    <tr key={po.id} className="record-row">
                      {canCreate && (
                        <td
                          className="record-select-cell"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            aria-label={`Select purchase order ${po.orderNumber || ''}`}
                            checked={selectedIds.includes(po.raw.poId)}
                            onChange={() => toggleSelected(po.raw.poId)}
                          />
                        </td>
                      )}
                      <td>{formatDate(po.date)}</td>
                      <td>
                        {po.orderNumber || '—'}
                        {po.title && <div className="record-doc-title">{po.title}</div>}
                      </td>
                      <td>{po.vendorName || '—'}</td>
                      <td>
                        <div className="record-status-cell">
                          <StatusBadge status={po.status} />
                          <ClientViewedBadge
                            firstViewedAt={po.raw?.clientFirstViewedAt}
                            lastViewedAt={po.raw?.clientLastViewedAt}
                            viewCount={po.raw?.clientViewCount}
                            sent={(po.status || '').toLowerCase() !== 'draft'}
                          />
                        </div>
                      </td>
                      <td>{formatCurrency(open)}</td>
                      <td>{formatCurrency(po.payments)}</td>
                      <td>{formatCurrency(po.total)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="folio-icon-btn"
                          type="button"
                          onClick={() => setShowFolio(po.raw)}
                          aria-label={`Open folio for ${po.orderNumber || 'purchase order'}`}
                        >
                          <img
                            src="/folder-icon.png"
                            alt="Open folio"
                            className="link-icon"
                            width={18}
                            height={18}
                            draggable={false}
                          />
                        </button>
                      </td>
                      <td>
                        <div className="actions-wrapper">
                          <button
                            className="primary-action"
                            onClick={() => navigate(`/projects/${projectId}/purchase-orders/${po.raw.poId}`)}
                          >
                            View PO
                          </button>
                          {canCreate && (
                            <DropdownMenu
                              qbBillUrl={
                                po.raw.quickbooks?.billId
                                  ? getQuickBooksBillUrl(po.raw.quickbooks.billId, po.raw.quickbooks.environment)
                                  : null
                              }
                              onSyncQuickBooks={() => syncToQuickBooks(po)}
                              syncingQuickBooks={syncingPoId === po.raw.poId}
                              onDownloadPdf={() =>
                                navigate(`/projects/${projectId}/purchase-orders/${po.raw.poId}?autoDownload=1`)
                              }
                              onDelete={() => deleteOne(po)}
                              canDelete={Number(po.payments || 0) <= 0}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <RecordPagination
          page={currentPage}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {showFolio && (
        <PurchaseOrderFolioModal
          purchaseOrder={showFolio}
          projectId={projectId}
          onClose={() => setShowFolio(null)}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        destructive={confirmModal.destructive}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </section>
  );
}

/* ---------------------- Status Badge ---------------------- */
function StatusBadge({ status }) {
  const colors = {
    draft: '#666',
    sent: '#1456d9',
    approved: '#0a7d33',
    ordered: '#0a7d33',
    received: '#0a7d33',
    paid: '#0a7d33',
    partial: '#b56a00',
    cancelled: '#b91c1c',
  };

  const safeStatus = (status || 'draft').toLowerCase();
  const display = safeStatus.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

  return (
    <span
      style={{
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        background: `${colors[safeStatus] || '#ccc'}22`,
        color: colors[safeStatus] || '#333',
        textTransform: 'capitalize',
      }}
    >
      {display}
    </span>
  );
}

SortIcon.propTypes = { active: PropTypes.bool, order: PropTypes.string };
PurchaseOrdersSection.propTypes = { projectId: PropTypes.string, project: PropTypes.object };
